import { Link, useLocation } from "@tanstack/react-router";
import { ChevronRight, Home } from "lucide-react";
import { useMemo } from "react";

interface Crumb {
  label: string;
  href?: string;
  key: string;
}

const SEGMENT_LABELS: Record<string, string> = {
  "/": "Dashboard",
  timeline: "Timeline",
  projects: "Projects",
  companies: "Companies",
  contacts: "Contacts",
  documents: "Documents",
  "pay-applications": "Pay Applications",
  subcontract: "Subcontract",
  owner: "Owner",
  "general-requirements": "General Requirements",
  intake: "Intake",
  inbox: "Inbox",
  "pending-vista": "Pending Vista",
  "my-company": "My Company",
  "my-commitments": "My Commitments",
  "my-pay-apps": "My Pay Apps",
  commitments: "Commitments",
  new: "New",
  compliance: "Compliance",
  rfis: "RFIs",
  submittals: "Submittals",
  "daily-logs": "Daily Logs",
  "punch-list": "Punch List",
  inspections: "Inspections",
  "job-cost": "Job Cost",
  admin: "System",
  config: "Configuration",
  users: "Users",
  audit: "Audit Trail",
  "cost-codes": "Cost Codes",
  "task-templates": "Task Templates",
  "milestone-templates": "Milestone Templates",
  "folder-templates": "Folder Templates",
  "project-team-roles": "Project Team Roles",
  glossary: "Glossary",
  "release-notes": "Release Notes",
  profile: "Profile",
};

function labelFor(segment: string): string {
  const mapped = SEGMENT_LABELS[segment];
  if (mapped) return mapped;
  // Param-like segments (IDs) — show truncated
  if (segment.length > 12) return `${segment.slice(0, 8)}…`;
  return segment;
}

function buildCrumbs(pathname: string): Crumb[] {
  if (pathname === "/" || pathname === "") {
    return [{ label: "Dashboard", key: "/" }];
  }
  const parts = pathname.split("/").filter(Boolean);
  const crumbs: Crumb[] = [];
  let acc = "";
  parts.forEach((seg, i) => {
    acc += `/${seg}`;
    const isLast = i === parts.length - 1;
    crumbs.push({ label: labelFor(seg), href: isLast ? undefined : acc, key: acc });
  });
  return crumbs;
}

export function Breadcrumbs() {
  const location = useLocation();
  const crumbs = useMemo(() => buildCrumbs(location.pathname), [location.pathname]);

  const MAX = 4;
  const truncated = crumbs.length > MAX;
  const shown = truncated ? crumbs.slice(-MAX) : crumbs;

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[12px] text-muted-foreground">
      <Link to="/" className="flex shrink-0 items-center hover:text-foreground" aria-label="Home">
        <Home className="h-3 w-3 opacity-60" />
      </Link>
      {truncated && (
        <>
          <span>…</span>
          <ChevronRight className="h-3 w-3 shrink-0 opacity-40" />
        </>
      )}
      {shown.map((c, i) => {
        const last = i === shown.length - 1;
        return (
          <span key={c.key} className="flex min-w-0 items-center gap-1.5">
            {c.href && !last ? (
              <Link to={c.href} className="truncate hover:text-foreground hover:underline">
                {c.label}
              </Link>
            ) : (
              <span
                className="truncate"
                style={{
                  color: last ? "var(--foreground)" : undefined,
                  fontWeight: last ? 500 : 400,
                }}
              >
                {c.label}
              </span>
            )}
            {!last && <ChevronRight className="h-3 w-3 shrink-0 opacity-40" />}
          </span>
        );
      })}
    </div>
  );
}
